import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const ThemeContext = createContext();

// 终端风格的暗色主题（保留 Bloomberg/Fincept 灵感）
const darkTheme = {
  name: 'dark',
  colors: {
    primary: '#FF7A00',
    primaryHover: '#B35400',
    secondary: '#0A0A0A',
    background: '#000000',
    surface: '#0A0A0A',
    surfaceHover: '#141414',
    border: '#2A2A2A',
    text: '#E6E6E6',
    textSecondary: '#9A9A9A',
    textMuted: '#5F5F5F',
    cardBg: '#0A0A0A',
    cardBorder: '#2A2A2A',
    accent: '#FF7A00',
    accentDim: '#B35400',
    borderStrong: '#3D3D3D',
    info: '#00B7FF',
    warn: '#FFD23F',
    fkeyBg: '#1A1A1A',
    fkeyActive: '#FF7A00',
    statusBg: '#0F0F0F',
    panelAlt: '#141414',
    chart: {
      grid: '#1A1A1A',
      axis: '#5F5F5F',
      line: '#FF7A00',
      area: '#FF7A00',
      gradientStart: 'rgba(255, 122, 0, 0.20)',
      gradientEnd: 'rgba(255, 122, 0, 0.02)',
      prediction: '#00B7FF',
      historical: '#FF7A00',
      confidence: 'rgba(0, 183, 255, 0.10)',
      colors: ['#FF7A00', '#00B7FF', '#FFD23F', '#9A9A9A', '#B35400', '#E6E6E6', '#00C853', '#FF3B3B']
    }
  }
};

// 终端风格的亮色主题（保留橙色品牌色，反转底色与文字）
const lightTheme = {
  name: 'light',
  colors: {
    primary: '#FF7A00',
    primaryHover: '#B35400',
    secondary: '#F5F5F5',
    background: '#FFFFFF',
    surface: '#FAFAFA',
    surfaceHover: '#F0F0F0',
    border: '#D6D6D6',
    text: '#1A1A1A',
    textSecondary: '#5F5F5F',
    textMuted: '#9A9A9A',
    cardBg: '#FFFFFF',
    cardBorder: '#D6D6D6',
    accent: '#E66C00',
    accentDim: '#B35400',
    borderStrong: '#BABABA',
    info: '#008CCC',
    warn: '#B89020',
    fkeyBg: '#ECECEC',
    fkeyActive: '#FF7A00',
    statusBg: '#F5F5F5',
    panelAlt: '#F0F0F0',
    chart: {
      grid: '#E5E5E5',
      axis: '#9A9A9A',
      line: '#E66C00',
      area: '#E66C00',
      gradientStart: 'rgba(255, 122, 0, 0.15)',
      gradientEnd: 'rgba(255, 122, 0, 0.02)',
      prediction: '#008CCC',
      historical: '#E66C00',
      confidence: 'rgba(0, 140, 204, 0.10)',
      colors: ['#E66C00', '#008CCC', '#B89020', '#5F5F5F', '#B35400', '#1A1A1A', '#00A248', '#D02828']
    }
  }
};

const THEMES = { dark: darkTheme, light: lightTheme };
const STORAGE_KEY = 'sagax_theme';

const readInitialTheme = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch { /* ignore */ }
  return 'dark';
};

export const ThemeProvider = ({ children }) => {
  const [themeName, setThemeName] = useState(readInitialTheme);
  const theme = useMemo(() => THEMES[themeName] || darkTheme, [themeName]);

  // 清理旧版 key（迁移残留），避免与新 sagax_theme 混淆
  useEffect(() => {
    try {
      localStorage.removeItem('theme');
      localStorage.removeItem('themeMigratedV2');
    } catch { /* ignore */ }
  }, []);

  // 应用 CSS 变量 + data-theme
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeName);
    const root = document.documentElement;
    Object.keys(theme.colors).forEach((key) => {
      const v = theme.colors[key];
      if (v && typeof v === 'object') {
        Object.keys(v).forEach((subKey) => {
          root.style.setProperty(`--color-${key}-${subKey}`, v[subKey]);
        });
      } else {
        root.style.setProperty(`--color-${key}`, v);
      }
    });
    try { localStorage.setItem(STORAGE_KEY, themeName); } catch { /* ignore */ }
  }, [themeName, theme]);

  const toggleTheme = useCallback(() => {
    setThemeName((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const setThemeByName = useCallback((name) => {
    if (name === 'dark' || name === 'light') setThemeName(name);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, themeName, toggleTheme, setThemeByName }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
